import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Store, Loader2, CheckCircle } from "lucide-react";

const TIPOS = ["Tienda de barrio", "Minimercado", "Panadería", "Cafetería", "Restaurante", "Supermercado", "Distribuidor", "Otro"];
const FACT_CAMPOS = [
  ["tipo_persona", "Tipo de persona (Natural/Jurídica)"],
  ["tipo_documento", "Tipo de documento (NIT/CC)"],
  ["numero_documento", "Número de documento"],
  ["razon_social", "Nombre / Razón social"],
  ["email_facturacion", "Email de facturación"],
  ["direccion_fiscal", "Dirección fiscal"],
  ["ciudad_fiscal", "Ciudad fiscal"],
];

export default function RegistroNegocioB2B() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    nombre_comercial: "", razon_social: "", nit: "", responsable: "", telefono: "", whatsapp: "",
    email: "", tipo_establecimiento: "", direccion: "", barrio: "", localidad: "", ciudad: "Bogotá",
    horarios_recepcion: "", requiere_factura: false, datos_facturacion: {}, acepta_terminos: false,
  });
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    document.title = "Baruj Shefa Negocios - Registro";
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFact = (k, v) => setForm((f) => ({ ...f, datos_facturacion: { ...f.datos_facturacion, [k]: v } }));

  const enviar = async () => {
    setEnviando(true);
    try {
      await api.post("/b2b/registro", { ...form, ref: params.get("ref") || "" });
      setListo(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos enviar tu registro");
    } finally {
      setEnviando(false);
    }
  };

  if (listo) {
    return (
      <div className="min-h-screen bg-[#FBF5E8] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center" data-testid="registro-negocio-exito">
          <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-3" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#4A2C0A] mb-2">¡Registro recibido!</h1>
          <p className="font-['Poppins'] text-sm text-[#7A6552]">Nuestro equipo comercial revisará tu solicitud y te contactará para activar tu cuenta mayorista con precios especiales.</p>
          <Link to="/" className="inline-block mt-5 text-sm font-bold text-[#C8902A]">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF5E8] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#4A2C0A] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Store className="w-7 h-7 text-[#C8902A]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#4A2C0A]">Baruj Shefa Negocios</h1>
          <p className="font-['Poppins'] text-sm text-[#7A6552] mt-1">Registra tu establecimiento y accede a precios mayoristas, entregas programadas y el Club de la Abuela Negocios</p>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-[#E5D4B3] p-5 space-y-3" data-testid="registro-negocio-form">
          <Input data-testid="neg-nombre-comercial" placeholder="Nombre comercial del establecimiento *" value={form.nombre_comercial} onChange={(e) => set("nombre_comercial", e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Razón social (opcional)" value={form.razon_social} onChange={(e) => set("razon_social", e.target.value)} />
            <Input placeholder="NIT / Identificación" value={form.nit} onChange={(e) => set("nit", e.target.value)} />
          </div>
          <Input data-testid="neg-responsable" placeholder="Nombre del responsable *" value={form.responsable} onChange={(e) => set("responsable", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="neg-telefono" placeholder="Teléfono *" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} />
            <Input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
          </div>
          <Input data-testid="neg-email" type="email" placeholder="Correo electrónico *" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <select data-testid="neg-tipo" value={form.tipo_establecimiento} onChange={(e) => set("tipo_establecimiento", e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
            <option value="">Tipo de establecimiento *</option>
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input data-testid="neg-direccion" placeholder="Dirección *" value={form.direccion} onChange={(e) => set("direccion", e.target.value)} />
          <div className="grid grid-cols-3 gap-3">
            <Input placeholder="Barrio" value={form.barrio} onChange={(e) => set("barrio", e.target.value)} />
            <Input placeholder="Localidad" value={form.localidad} onChange={(e) => set("localidad", e.target.value)} />
            <Input placeholder="Ciudad *" value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} />
          </div>
          <Textarea rows={2} placeholder="Horarios de recepción de mercancía (opcional)" value={form.horarios_recepcion} onChange={(e) => set("horarios_recepcion", e.target.value)} />

          <label className="flex items-center gap-2 pt-1 cursor-pointer">
            <Checkbox data-testid="neg-factura-check" checked={form.requiere_factura} onCheckedChange={(v) => set("requiere_factura", !!v)} />
            <span className="font-['Poppins'] text-sm text-[#4A2C0A]">Requiero factura electrónica</span>
          </label>
          {form.requiere_factura && (
            <div className="space-y-2 bg-[#FBF5E8] rounded-xl p-3" data-testid="neg-factura-campos">
              <select data-testid="neg-fact-tipo_persona" value={form.datos_facturacion.tipo_persona || ""} onChange={(e) => setFact("tipo_persona", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                <option value="">Tipo de persona *</option>
                <option value="Natural">Natural</option>
                <option value="Jurídica">Jurídica</option>
              </select>
              <select data-testid="neg-fact-tipo_documento" value={form.datos_facturacion.tipo_documento || ""} onChange={(e) => setFact("tipo_documento", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                <option value="">Tipo de documento *</option>
                <option value="NIT">NIT</option>
                <option value="CC">Cédula de ciudadanía</option>
                <option value="CE">Cédula de extranjería</option>
              </select>
              {FACT_CAMPOS.filter(([k]) => !["tipo_persona", "tipo_documento"].includes(k)).map(([k, l]) => (
                <Input key={k} data-testid={`neg-fact-${k}`} placeholder={l + " *"} value={form.datos_facturacion[k] || ""} onChange={(e) => setFact(k, e.target.value)} className="bg-white" />
              ))}
            </div>
          )}

          <label className="flex items-start gap-2 pt-1 cursor-pointer">
            <Checkbox data-testid="neg-terminos-check" checked={form.acepta_terminos} onCheckedChange={(v) => set("acepta_terminos", !!v)} className="mt-0.5" />
            <span className="font-['Poppins'] text-xs text-[#7A6552]">Acepto los términos comerciales y el tratamiento de mis datos personales por Baruj Shefa SAS *</span>
          </label>

          <Button data-testid="neg-enviar-btn" onClick={enviar} disabled={enviando} className="w-full h-12 bg-[#C8902A] hover:bg-[#B07D1E] rounded-xl font-bold">
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar mi negocio"}
          </Button>
          <p className="font-['Poppins'] text-[11px] text-center text-[#7A6552]">Venta en firme · Entregas programadas en Bogotá · Pago contra entrega</p>
        </div>
      </div>
    </div>
  );
}
